import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { AccountContext } from '../../../../components/AccountContext';
import { growlErrors, growlActionSuccess } from '../../../../utils/NotificationUtil';
import { Loading } from '../../../../components/Loading';
import RespondentEditForm from './RespondentEditForm';
import { NotificationContext } from '../../../../components/GrowlNotifications';
import { validationMessages } from '../../../../utils/FormUtils';
import { useSelector } from 'react-redux';

import RespondentEmploymentConfirmationDialog from './RespondentEmploymentConfirmationDialog';

const messages = defineMessages({
  successNoti: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
});

const RespondentEdit = ({ toggleEdit, updateRespondent, respondent, localeList }) => {
  const [displayEmploymentConfirmationDialog, setDisplayEmploymentConfirmationDialog] = useState(false);

  const { formatMessage } = useIntl();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);

  const {
    cldr: { localeDisplayName },
  } = useSelector((state) => state.i18n);

  const saveEditRespondent = async (value, resetForm) => {
    try {
      await updateRespondent({
        variables: {
          input: {
            ...value,
            accountId: currentAccount,
          },
        },
      });
      growlActionSuccess(growl, formatMessage(messages.successNoti));
      toggleEdit('core');
    } catch (err) {
      growlErrors(growl, err);
    } finally {
      resetForm();
    }
  };

  const initialValues = {
    respondentId: respondent.id,
    firstName: respondent.firstName,
    lastName: respondent.lastName,
    email: respondent.email,
    gender: respondent.gender,
    locale: respondent.locale,
    type: respondent.type,
    notes: respondent.notes,
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(formatMessage(validationMessages.required)),
    lastName: Yup.string().required(formatMessage(validationMessages.required)),
    email: Yup.string()
      .required(formatMessage(validationMessages.required))
      .email(formatMessage(validationMessages.email)),
    gender: Yup.string().required(formatMessage(validationMessages.required)),
    type: Yup.string().required(formatMessage(validationMessages.required)),
  });

  const { loading, activeLocales } = localeList;

  if (loading) {
    return <Loading />;
  }

  const preferedLocales = activeLocales.map((lang) => ({
    value: lang,
    label: localeDisplayName(lang),
  }));

  return (
    <>
      <RespondentEmploymentConfirmationDialog
        displayEmploymentConfirmationDialog={displayEmploymentConfirmationDialog}
        respondentId={respondent.id}
        hide={() => setDisplayEmploymentConfirmationDialog(false)}
      />
      <div className="panel panel-no-background">
        <div className="panel-body">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values, formikBag) => {
              const { resetForm } = formikBag;
              saveEditRespondent(values, resetForm);
            }}
          >
            <RespondentEditForm
              toggleEdit={toggleEdit}
              preferedLocales={preferedLocales}
              respondent={respondent}
              showEmploymentConfirmationModal={() => {
                setDisplayEmploymentConfirmationDialog(true);
              }}
            />
          </Formik>
        </div>
      </div>
    </>
  );
};

RespondentEdit.propTypes = {
  localeList: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    viewer: PropTypes.object,
  }).isRequired,
  toggleEdit: PropTypes.func.isRequired,
  updateRespondent: PropTypes.func.isRequired,
  respondent: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default RespondentEdit;
